import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { breakpoints } from '../constants/mediaqueries';
import { SPACING_VERTICAL } from '../constants/spacings';
import { bulletStyles, fontStyles, linkStyles } from '../constants/styles';
import { isInternalURI } from '../helpers/location';
import { fluidPropTypes } from '../types';

import { customMarkdown } from '../helpers/markdown';

import ExternalLink from './ExternalLink';
import ImageTitleCaption from './ImageTitleCaption';
import LocalisedLink from './LocalisedLink';
import MichelbergerImage from './MichelbergerImage';


export const Wrapper = styled.div`
  @media ${breakpoints.l} {
    display: flex;
    justify-content: space-between;

    ${(props) => (props.index % 2 === 0 ? 'flex-direction: row-reverse;' : '')}
  }
`;

export const Image = styled.div`
  margin-bottom: calc(1rem + .2vw);

  > a {
    display: block;
  }

  @media ${breakpoints.l} {
    width: calc(50% - 4vw);
    margin-bottom: 0;
  }
`;

export const MarkdownContent = styled.div`
  ${SPACING_VERTICAL.s};
  ${fontStyles.bodySmall}

  @media ${breakpoints.l} {
    width: calc(50% - 4vw);
    margin-bottom: 0;
  }

  a {
    ${linkStyles}
  }

  ul li {
    ${bulletStyles}
  }

  h1, h2 {
    ${fontStyles.body}
    margin-bottom: 1rem;

    @media ${breakpoints.l} {
      margin-bottom: 1.5rem;
    }
  }

  h3 {
    ${fontStyles.uppercase}
  }

  ${(props) => props.usesH3StylingForH2
    && `
      h2 {
        ${fontStyles.uppercase}
        margin-bottom: 0;
      }
    `
}

  /* Spacing */
  * + * {
    margin-top: 1em; /* Generic spacing rule */
  }

  * + ul,
  * + ol,
  * + li {
    margin-top: 0;
  }

  * + h1,
  * + h1 {
    margin-top: 2rem;

    @media ${breakpoints.l} {
      margin-top: 5rem;
    }
  }
`;

const ImageMarkdown = ({ block, index }) => {
  const { caption, changesColorOnHover, hasBlendMode, image, imageUrl, text, title, usesH3StylingForH2 } = block;

  const mbImage = (
    <MichelbergerImage
      changesColorOnHover={changesColorOnHover}
      hasBlendMode={hasBlendMode}
      image={image}
    />
  );

  return (
    <Wrapper index={index}>
      <If condition={text}>
        <MarkdownContent usesH3StylingForH2={usesH3StylingForH2}>{customMarkdown(text)}</MarkdownContent>
      </If>
      <Image>
        <Choose>
          <When condition={imageUrl && isInternalURI(imageUrl)}>
            <LocalisedLink hasUnderline={false} to={imageUrl}>{mbImage}</LocalisedLink>
          </When>
          <When condition={imageUrl}>
            <ExternalLink hasUnderline={false} href={imageUrl}>{mbImage}</ExternalLink>
          </When>
          <Otherwise>
            {mbImage}
          </Otherwise>
        </Choose>
        <ImageTitleCaption caption={caption} title={title} url={imageUrl} />
      </Image>
    </Wrapper>
  );
};

ImageMarkdown.propTypes = {
  index: PropTypes.number.isRequired,
  block: PropTypes.shape({
    id: PropTypes.string,
    caption: PropTypes.string,
    changesColorOnHover: PropTypes.bool,
    hasBlendMode: PropTypes.bool,
    usesH3StylingForH2: PropTypes.bool,
    image: PropTypes.shape({
      id: PropTypes.string,
      mimeType: PropTypes.string,
      url: PropTypes.string,
      fluid: fluidPropTypes,
    }),
    imageUrl: PropTypes.string,
    text: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default ImageMarkdown;
